import { AnimalSplitPart } from '@farmshare/utils';

import { QuickScheduleForm } from './types';

export function isMissing(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * True when the user has entered any data about animal `i`. Hang time is
 * intentionally excluded — ReviewStep auto-pre-selects it from the producer's
 * preferences on mount, so treating it as a touched signal would flag every
 * animal as touched before the user does anything. `isOverThirtyMonths` is
 * similarly excluded because it can auto-default.
 *
 * Used to gate required-head-field validation, which is otherwise applied by
 * both the top-level Formik validator and per-input field validators on the
 * head custom fields.
 * @param values
 * @param i
 */
export function isAnimalTouched(values: QuickScheduleForm, i: number): boolean {
  const headCfv = values.head?.[i]?.customFieldValues;
  if (headCfv && Object.values(headCfv).some((v) => !isMissing(v))) return true;
  if (!isMissing(values.producerIdentifiers?.[i])) return true;
  if (!isMissing(values.sexes?.[i])) return true;
  if (!isMissing(values.splitTypes?.[i])) return true;
  return false;
}

// Canonical AnimalSplitPart enum values persisted on splits (sent via the
// cutsheetInformation mutation input).
// Aliased from the shared type so it stays in sync if supported parts change.
type SplitPartValue = AnimalSplitPart;

interface SplitSlot {
  label: string;
  splitPart: SplitPartValue;
}

export function getSplitSlotsForType(splitType: string): SplitSlot[] {
  switch (splitType?.toLowerCase()) {
    case 'whole':
      return [{ label: 'Whole', splitPart: 'whole' }];
    case 'half':
      return [
        { label: 'Half 1', splitPart: 'half' },
        { label: 'Half 2', splitPart: 'half' },
      ];
    case 'quarters':
      return [
        { label: 'Quarter 1', splitPart: 'quarter' },
        { label: 'Quarter 2', splitPart: 'quarter' },
        { label: 'Quarter 3', splitPart: 'quarter' },
        { label: 'Quarter 4', splitPart: 'quarter' },
      ];
    case 'half_and_two_quarters':
      return [
        { label: 'Half', splitPart: 'half' },
        { label: 'Quarter 1', splitPart: 'quarter' },
        { label: 'Quarter 2', splitPart: 'quarter' },
      ];
    default:
      return [];
  }
}
